import { MISIONES, type Mision } from './misiones';
import type { ProgresoDeMisiones } from './progreso-de-misiones';

/**
 * Mini Prixia (X3): la ÚNICA función que decide cómo se ve una misión en la tarjeta, y
 * la del aviso de orden. La tabla de estados está en el plan de la Ronda X;
 * `estado-de-mision.test.ts` la recorre.
 */
export type EstadoDeMision = 'pendiente' | 'en-curso' | 'completa';

export interface VistaDeMision {
  estado: EstadoDeMision;
  /** Su recorrido está abierto ahora. */
  tourAbierto: boolean;
  /** El texto de su botón: siempre se puede (re)hacer el recorrido (decisión 21). */
  boton: 'Empezar' | 'Seguir' | 'Repetir';
  /** «Te falta …» sólo en curso y con el recorrido cerrado (decisión 22). */
  falta: string | null;
}

/** Para el lector de pantalla (el círculo solo es un dibujo). */
export const ESTADO_EN_PALABRAS: Record<EstadoDeMision, string> = {
  pendiente: 'Pendiente',
  'en-curso': 'En curso',
  completa: 'Cumplida',
};

export function vistaDeMision(
  mision: Pick<Mision, 'id' | 'tour' | 'falta'>,
  progreso: ProgresoDeMisiones,
  tour: { isActive: boolean; currentTourId: string | null }
): VistaDeMision {
  const tourAbierto = tour.isActive && tour.currentTourId === mision.tour;
  if (progreso.hechas.includes(mision.id))
    return { estado: 'completa', tourAbierto, boton: 'Repetir', falta: null };
  if (tourAbierto || progreso.empezadas.includes(mision.id))
    return {
      estado: 'en-curso',
      tourAbierto,
      boton: 'Seguir',
      falta: tourAbierto ? null : mision.falta,
    };
  return { estado: 'pendiente', tourAbierto: false, boton: 'Empezar', falta: null };
}

/** Orden sugerido (respuesta de Sebastián): la misión previa que falta, o `null`. */
export function misionPrevia(
  mision: Pick<Mision, 'requiere'>,
  progreso: ProgresoDeMisiones
): Mision | null {
  if (!mision.requiere || progreso.hechas.includes(mision.requiere)) return null;
  return MISIONES.find((m) => m.id === mision.requiere) ?? null;
}

/** «Primero carga tu catálogo», «Primero crea una estrategia». */
export function textoDePrimero(previa: Pick<Mision, 'titulo'>): string {
  return `Primero ${previa.titulo.charAt(0).toLowerCase()}${previa.titulo.slice(1)}`;
}
